import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, Instagram, Facebook, Calendar, DollarSign, Sparkles, Info, Camera, ListChecks, ArrowRight } from 'lucide-react';
import GlowChat from './GlowChat';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_b78645e2-7e54-4fc5-b6dd-84c8c35b28ff/artifacts/60ni2i2y_FullLogo%20%281%29.jpg";

// Update these to your BUSINESS accounts when ready.
// Leave as empty string to hide the button entirely (prevents personal-profile leaks).
const INSTAGRAM_URL = "https://instagram.com/christinamariesunshine";
const FACEBOOK_URL = "";

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Plans', path: '/plans' },
  { label: 'Services', path: '/services' },
  { label: 'Gallery', path: '/gallery' },
  { label: 'How It Works', path: '/how-it-works' },
  { label: 'About', path: '/about' },
];

const PublicLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Scroll to top on every route change
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#0a0405] text-[#f4e9d8] font-sans" data-testid="public-layout">
      {/* Top Bar */}
      <div className="bg-black/60 border-b border-amber-900/30 text-[10px] tracking-[0.25em] uppercase">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between text-amber-200/70">
          <span data-testid="topbar-tagline">✦ 24K Luxury, On The Go ✦</span>
          <a href="tel:9046729966" className="hidden sm:flex items-center gap-2 hover:text-amber-200" data-testid="topbar-phone">
            <Phone className="w-3 h-3" /> 904.672.9966
          </a>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0a0405]/95 backdrop-blur-md border-b border-amber-900/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img src={LOGO_URL} alt="Monthly Glow On The Go" className="w-12 h-12 object-contain" />
            <div className="hidden sm:block leading-tight">
              <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/70">Monthly Glow</div>
              <div className="font-serif text-xl text-amber-100">On The Go</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="header-nav">
            {navLinks.map(l => (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.path === '/'}
                className={({ isActive }) =>
                  `text-sm tracking-wider uppercase transition-colors ${isActive ? 'text-pink-300' : 'text-amber-100/70 hover:text-amber-200'}`
                }
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm text-amber-200/80 hover:text-amber-100 px-4 py-2"
              data-testid="header-signin"
            >
              Sign In
            </Link>
            <Link
              to="/plans"
              className="px-5 py-2.5 rounded-full bg-gradient-to-r from-pink-400 to-pink-300 text-black font-medium text-sm hover:from-pink-300 hover:to-pink-200 transition-all shadow-lg shadow-pink-500/20"
              data-testid="header-subscribe"
            >
              Subscribe
            </Link>
          </div>

          {/* Mobile menu trigger */}
          <button
            className="lg:hidden text-amber-100 p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-amber-900/30 bg-[#0a0405]" data-testid="mobile-nav">
            <nav className="px-6 py-4 space-y-3">
              {navLinks.map(l => (
                <NavLink
                  key={l.path}
                  to={l.path}
                  end={l.path === '/'}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `block text-sm tracking-wider uppercase py-2 ${isActive ? 'text-pink-300' : 'text-amber-100/70'}`
                  }
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="pt-3 border-t border-amber-900/30 flex flex-col gap-2">
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="text-center text-amber-200/80 py-2 text-sm"
                  data-testid="mobile-signin"
                >
                  Sign In
                </Link>
                <Link
                  to="/plans"
                  onClick={() => setMobileOpen(false)}
                  className="text-center px-5 py-2.5 rounded-full bg-pink-400 text-black font-medium text-sm"
                  data-testid="mobile-subscribe"
                >
                  Subscribe
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Page Content */}
      <main key={location.pathname}>{children}</main>

      {/* PRE-FOOTER: Big Explore CTAs — click-driving */}
      <section className="border-t border-amber-900/30 bg-gradient-to-b from-black/80 to-[#0a0405]" data-testid="explore-section">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <div className="text-[10px] tracking-[0.35em] uppercase text-amber-300/70 mb-3">✦ Explore ✦</div>
            <h3 className="font-serif text-3xl sm:text-4xl text-amber-50">Where to <span className="italic text-pink-300">next?</span></h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {[
              { to: '/book', icon: Calendar, label: 'Book Now', sub: 'Same-day booking', pop: true },
              { to: '/plans', icon: Sparkles, label: 'Plans', sub: 'From $55/mo' },
              { to: '/services', icon: ListChecks, label: 'Services', sub: 'Full menu + pricing' },
              { to: '/gallery', icon: Camera, label: 'Gallery', sub: 'Real client work' },
              { to: '/nail-cash', icon: DollarSign, label: '$10 Nail Cash', sub: 'Refer & earn', pop: true },
              { to: '/how-it-works', icon: ArrowRight, label: 'How It Works', sub: '4 easy steps' },
              { to: '/about', icon: Info, label: 'About', sub: 'Our story' },
              { to: '/login', icon: Sparkles, label: 'Sign In', sub: 'Admin & clients' },
            ].map(t => (
              <Link
                key={t.to}
                to={t.to}
                data-testid={`explore-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}
                className={`group relative flex flex-col justify-between rounded-2xl p-5 sm:p-6 min-h-[130px] transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl border ${
                  t.pop
                    ? 'bg-gradient-to-br from-pink-300 to-pink-400 border-pink-200 hover:shadow-pink-500/30'
                    : 'bg-gradient-to-br from-amber-950/40 to-black/70 border-amber-700/40 hover:border-amber-400/70 hover:shadow-amber-500/10'
                }`}
              >
                <t.icon className={`w-6 h-6 ${t.pop ? 'text-black' : 'text-amber-300'}`} />
                <div>
                  <div className={`font-serif text-lg sm:text-xl leading-tight ${t.pop ? 'text-black' : 'text-amber-50'}`}>{t.label}</div>
                  <div className={`text-[11px] tracking-wide mt-1 ${t.pop ? 'text-black/70' : 'text-amber-100/60'}`}>{t.sub}</div>
                </div>
                <ArrowRight className={`absolute top-4 right-4 w-4 h-4 transition-transform group-hover:translate-x-1 ${t.pop ? 'text-black/70' : 'text-amber-300/60'}`} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-amber-900/30 bg-black/80" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="Logo" className="w-14 h-14 object-contain" />
              <div>
                <div className="font-serif text-2xl text-amber-100">Monthly Glow On The Go</div>
                <div className="text-xs tracking-[0.25em] uppercase text-amber-300/60 mt-1">24K Luxury · Delivered</div>
              </div>
            </div>
            <p className="text-sm text-amber-100/60 max-w-md leading-relaxed">
              Luxury mobile nails, spray tans, facials, makeup & hair styling — brought directly to your door across Northeast Florida & Southern Georgia.
            </p>
            <div className="flex items-center gap-4 mt-6">
              {INSTAGRAM_URL && (
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-amber-700/50 flex items-center justify-center text-amber-200/80 hover:bg-amber-900/30 hover:border-amber-400/70 transition-all" data-testid="footer-instagram">
                  <Instagram className="w-4 h-4" />
                </a>
              )}
              {FACEBOOK_URL && (
                <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-amber-700/50 flex items-center justify-center text-amber-200/80 hover:bg-amber-900/30 hover:border-amber-400/70 transition-all" data-testid="footer-facebook">
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {!INSTAGRAM_URL && !FACEBOOK_URL && (
                <span className="text-xs text-amber-100/40 italic">Social links coming soon</span>
              )}
            </div>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/70 mb-4">Contact</div>
            <ul className="space-y-3 text-sm text-amber-100/80">
              <li>
                <a href="tel:9046729966" className="flex items-center gap-3 hover:text-amber-200 transition-colors" data-testid="footer-phone">
                  <span className="w-8 h-8 rounded-full bg-amber-900/30 border border-amber-700/40 flex items-center justify-center flex-shrink-0"><Phone className="w-3.5 h-3.5 text-amber-300" /></span>
                  <span>904.672.9966</span>
                </a>
              </li>
              <li>
                <a href="mailto:glowsupport@monthlyglowonthego.com" className="flex items-center gap-3 hover:text-amber-200 transition-colors" data-testid="footer-email">
                  <span className="w-8 h-8 rounded-full bg-amber-900/30 border border-amber-700/40 flex items-center justify-center flex-shrink-0"><Mail className="w-3.5 h-3.5 text-amber-300" /></span>
                  <span className="break-all text-xs sm:text-sm">glowsupport@monthlyglowonthego.com</span>
                </a>
              </li>
              <li className="text-xs text-amber-100/50 pt-2 pl-11">
                Serving all of Northeast FL & Southern GA
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-amber-900/30 py-6 text-center text-xs text-amber-100/40">
          © {new Date().getFullYear()} Monthly Glow On The Go · Owned & operated by Christina Sunshine
        </div>
      </footer>

      {/* AI Chat Assistant (floating) */}
      <GlowChat />
    </div>
  );
};

export default PublicLayout;
